import { useState } from 'react';
import { Box, ButtonBase, CircularProgress, Modal, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { wizardApi } from '@/services/wizard.api';
import type { PlanimetryDescriptor } from '@/types';

interface ElectricalPlanButtonProps {
  configurationId: number;
}

const LEGEND_SRC = `${import.meta.env.BASE_URL}legenda-elettrico.png`;

/**
 * Bottone "Guarda la planimetria dell'impianto elettrico" (card configurazione di partenza, fase
 * TECNOLOGIA): apre un viewer full-screen (stile ImageZoom legacy) con la vista `elettrico`
 * (`GET /planimetry?view=elettrico` → composeUrl del Worker) e la legenda `public/legenda-elettrico.png`.
 */
export function ElectricalPlanButton({ configurationId }: ElectricalPlanButtonProps) {
  const [open, setOpen] = useState(false);
  const [descriptor, setDescriptor] = useState<PlanimetryDescriptor | null>(null);
  const [status, setStatus] = useState<'idle' | 'loading' | 'ready' | 'error'>('idle');

  const handleOpen = async (): Promise<void> => {
    setOpen(true);
    if (descriptor || status === 'loading') return;
    setStatus('loading');
    try {
      const d = await wizardApi.getPlanimetry(configurationId, 'elettrico');
      setDescriptor(d);
      setStatus('ready');
    } catch {
      setStatus('error');
    }
  };

  const src = descriptor?.composeUrl ?? null;

  return (
    <>
      <ButtonBase
        onClick={() => void handleOpen()}
        sx={{
          alignSelf: 'flex-start',
          fontFamily: '"miller", serif',
          fontStyle: 'italic',
          fontSize: 14,
          color: 'primary.main',
          textDecoration: 'underline',
          mt: 0.5,
        }}
      >
        Guarda la planimetria dell&apos;impianto elettrico
      </ButtonBase>

      <Modal
        open={open}
        onClose={() => setOpen(false)}
        slotProps={{ backdrop: { sx: { bgcolor: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(15px)' } } }}
      >
        <Box
          sx={{
            position: 'fixed',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            p: { xs: 2, md: '30px' },
            outline: 'none',
          }}
        >
          <ButtonBase
            onClick={() => setOpen(false)}
            aria-label="Chiudi"
            sx={{
              position: 'absolute',
              top: 30,
              right: 30,
              width: 50,
              height: 50,
              borderRadius: '50%',
              bgcolor: '#000',
              zIndex: 10,
            }}
          >
            <CloseIcon sx={{ color: 'primary.main', fontSize: 20 }} />
          </ButtonBase>

          {status === 'loading' && <CircularProgress sx={{ color: '#fff' }} />}
          {(status === 'error' || (status === 'ready' && !src)) && (
            <Typography sx={{ color: '#fff' }}>
              La planimetria dell&apos;impianto elettrico non è disponibile.
            </Typography>
          )}
          {status === 'ready' && src && (
            <Box
              component="img"
              src={src}
              alt="Planimetria dell'impianto elettrico"
              sx={{ maxWidth: '80vw', maxHeight: '80vh', objectFit: 'contain' }}
            />
          )}

          {/* Legenda impianto elettrico (in basso a sinistra, come nel legacy ImageZoom). */}
          {status === 'ready' && src && (
            <Box
              component="img"
              src={LEGEND_SRC}
              alt="Legenda impianto elettrico"
              sx={{
                position: 'absolute',
                left: 30,
                bottom: 30,
                height: '30%',
                minHeight: 200,
                boxShadow: 'rgba(149,157,165,0.3) 0px 8px 24px',
              }}
            />
          )}
        </Box>
      </Modal>
    </>
  );
}
